import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Link, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { client, type AppRequest } from '../lib/api';
import { RequestStatusChip } from '../components/StatusChip';
import { PageHeader, Section } from '../components/Page';
import { useAuth } from '../lib/AuthContext';
import { tokens } from '../theme';

const DEPARTMENTS = ['IT', 'Production Control', 'Engineering', 'Quality', 'HR', 'Finance', 'Other'];

const STEPS = [
  { t: 'We review it', d: 'IT Lab checks scope and overlap with existing apps.' },
  { t: 'We meet you', d: 'A scoping session near your preferred date.' },
  { t: 'We build it', d: 'With check-ins along the way.' },
  { t: 'It goes live', d: 'The app joins the catalog and the status page.' },
];

const STAGES = ['submitted', 'reviewing', 'scheduled', 'in_progress', 'delivered'];

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <Stack spacing={0.75}>
      <Typography component="label" htmlFor={id} fontWeight={700} fontSize={18} color={tokens.ink}>
        {label}
      </Typography>
      {hint && (
        <Typography id={`${id}-hint`} fontSize={15} color="text.secondary">
          {hint}
        </Typography>
      )}
      {children}
    </Stack>
  );
}

const bigInput = { '& .MuiInputBase-root': { fontSize: 17 }, '& .MuiInputBase-input': { py: 1.75 } };

export function RequestPage() {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [department, setDepartment] = useState(
    DEPARTMENTS.includes(user?.department ?? '') ? (user?.department as string) : '',
  );
  const [description, setDescription] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [requests, setRequests] = useState<AppRequest[]>([]);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    const data = await client.requests();
    setRequests(data.requests);
  }

  useEffect(() => {
    if (!user) return;
    load().catch((err) => setError(err instanceof Error ? err.message : 'Failed to load'));
  }, [user]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await client.createRequest({
        title,
        department,
        description,
        preferredDate: preferredDate || null,
      });
      setTitle('');
      setDescription('');
      setPreferredDate('');
      setSent(true);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Submit failed');
    } finally {
      setBusy(false);
    }
  }

  if (!user) {
    return (
      <>
        <PageHeader title="Request an app" subtitle="Tell us what’s slowing your team down. It takes about 5 minutes." />
        <Section py={{ xs: 6, md: 8 }}>
          <Stack spacing={2} sx={{ maxWidth: 640, p: 4, border: `1px solid ${tokens.border}`, borderRadius: 2 }}>
            <Typography variant="h2" fontSize={26}>
              Sign in to request an app
            </Typography>
            <Typography color="text.secondary">
              We link each request to your account so we can book a scoping session and keep you updated on
              progress.
            </Typography>
            <Box pt={1}>
              <Button component={RouterLink} to="/login" variant="contained" size="large">
                Sign in
              </Button>
            </Box>
          </Stack>
        </Section>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Request an app" subtitle="Tell us what’s slowing your team down. It takes about 5 minutes." />

      <Section py={{ xs: 6, md: 8 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={{ xs: 6, md: 8 }} alignItems="flex-start">
          <Box sx={{ flex: 1.6, minWidth: 0, width: '100%', maxWidth: 720 }}>
            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}

            {sent ? (
              <Stack
                role="status"
                spacing={1.5}
                sx={{ border: '2px solid #079455', borderRadius: 2, p: 4, bgcolor: '#F6FEF9' }}
              >
                <Typography variant="h2" fontSize={28}>
                  Request submitted
                </Typography>
                <Typography color="#344054">
                  Thanks — IT Lab will review it and confirm your scoping session. You can follow its progress
                  below.
                </Typography>
                <Stack direction="row" gap={3} flexWrap="wrap" pt={1}>
                  <Link component={RouterLink} to="/">
                    Back to home
                  </Link>
                  <Link component="button" type="button" onClick={() => setSent(false)} fontSize={16}>
                    Submit another request
                  </Link>
                </Stack>
              </Stack>
            ) : (
              <Stack component="form" spacing={4} onSubmit={onSubmit}>
                <Field id="req-title" label="Request title" hint="A short name for the tool you need.">
                  <TextField
                    id="req-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    fullWidth
                    placeholder="e.g. Shift handover checklist for Line 3"
                    sx={bigInput}
                    slotProps={{ htmlInput: { 'aria-describedby': 'req-title-hint' } }}
                  />
                </Field>
                <Field id="req-dept" label="Department">
                  <TextField
                    id="req-dept"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    select
                    sx={{ ...bigInput, maxWidth: 400 }}
                    slotProps={{ select: { displayEmpty: true } }}
                  >
                    <MenuItem value="" disabled>
                      Select a department
                    </MenuItem>
                    {DEPARTMENTS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                </Field>
                <Field
                  id="req-desc"
                  label="What should the app do?"
                  hint="Who uses it, what they do today, and any spreadsheets or systems it should replace."
                >
                  <TextField
                    id="req-desc"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={6}
                    sx={{ '& .MuiInputBase-root': { fontSize: 17 } }}
                    slotProps={{ htmlInput: { 'aria-describedby': 'req-desc-hint' } }}
                  />
                </Field>
                <Field
                  id="req-date"
                  label="Preferred appointment date (optional)"
                  hint="We’ll book a short scoping session as close to this date as we can."
                >
                  <TextField
                    id="req-date"
                    type="date"
                    value={preferredDate}
                    onChange={(e) => setPreferredDate(e.target.value)}
                    sx={{ ...bigInput, maxWidth: 260 }}
                    slotProps={{ htmlInput: { 'aria-describedby': 'req-date-hint' } }}
                  />
                </Field>
                <Box pt={1}>
                  <Button type="submit" variant="contained" size="large" disabled={busy} sx={{ fontWeight: 700 }}>
                    {busy ? 'Submitting…' : 'Submit request'}
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>

          <Stack component="aside" spacing={3} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Typography variant="h2" fontSize={22}>
              What happens next
            </Typography>
            <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none' }}>
              {STEPS.map((s, i) => (
                <Box component="li" key={s.t} sx={{ display: 'grid', gridTemplateColumns: '40px 1fr', gap: 2, pb: 3 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: tokens.tint,
                      color: tokens.blueDeep,
                      display: 'grid',
                      placeItems: 'center',
                      fontWeight: 700,
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box>
                    <Typography fontWeight={600} color={tokens.ink}>
                      {s.t}
                    </Typography>
                    <Typography fontSize={15} color="text.secondary">
                      {s.d}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            <Box sx={{ p: 3, borderRadius: 2, bgcolor: tokens.subtle, border: `1px solid ${tokens.divider}` }}>
              <Typography fontWeight={600} color={tokens.ink}>
                Need help with an existing app?
              </Typography>
              <Typography fontSize={15} color="text.secondary">
                Check the{' '}
                <Link component={RouterLink} to="/status">
                  status page
                </Link>{' '}
                first.
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Section>

      <Section band="subtle" py={{ xs: 6, md: 9 }}>
        <Stack spacing={3}>
          <Typography variant="h2" sx={{ fontSize: { xs: 26, md: 32 } }}>
            {user?.role === 'admin' ? 'All requests' : 'Your requests'}
          </Typography>
          {requests.length === 0 ? (
            <Typography color="text.secondary">You haven’t submitted any requests yet.</Typography>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              {requests.map((r) => {
                const stage = STAGES.indexOf(r.status);
                return (
                  <Stack key={r.id} spacing={1.5} sx={{ p: 3, bgcolor: '#fff', border: `1px solid ${tokens.border}`, borderRadius: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                      <Box minWidth={0}>
                        <Typography fontWeight={700} fontSize={17} color={tokens.ink}>
                          {r.title}
                        </Typography>
                        <Typography fontSize={14} color="text.secondary">
                          {r.department || '—'} · submitted {new Date(r.created_at).toLocaleDateString()}
                          {r.requester_email ? ` · ${r.requester_name} (${r.requester_email})` : ''}
                        </Typography>
                      </Box>
                      <RequestStatusChip status={r.status} />
                    </Stack>
                    {r.status !== 'declined' && stage >= 0 && (
                      <Box
                        role="img"
                        aria-label={`Progress: step ${stage + 1} of ${STAGES.length}`}
                        sx={{ display: 'grid', gridTemplateColumns: `repeat(${STAGES.length}, minmax(0, 1fr))`, gap: 0.5 }}
                      >
                        {STAGES.map((s, i) => (
                          <Box key={s} sx={{ height: 4, borderRadius: 1, bgcolor: i <= stage ? tokens.blue : '#DCE3EA' }} />
                        ))}
                      </Box>
                    )}
                    <Typography fontSize={15} color="#344054" sx={{ whiteSpace: 'pre-line' }}>
                      {r.description}
                    </Typography>
                    {r.appointment_at && (
                      <Typography fontSize={14} fontWeight={600} color="primary.main">
                        Scoping session: {new Date(r.appointment_at).toLocaleString()}
                      </Typography>
                    )}
                    {r.admin_notes && (
                      <Typography fontSize={14} color="text.secondary">
                        Note from IT Lab: {r.admin_notes}
                      </Typography>
                    )}
                  </Stack>
                );
              })}
            </Box>
          )}
        </Stack>
      </Section>
    </>
  );
}
